import * as RTabs from '@radix-ui/react-tabs'
import type { ReactNode } from 'react'
import { cn } from '../lib/cn'
import { focusRing } from '../lib/focus'

export interface TabItem {
  value: string
  label: ReactNode
  content: ReactNode
  disabled?: boolean
  /** Trailing count/badge, e.g. review-queue depth. */
  adornment?: ReactNode
}

export interface TabsProps {
  items: TabItem[]
  value?: string
  defaultValue?: string
  onValueChange?: (value: string) => void
  /** `line` for page-level tabs (study detail), `pill` for in-card switches. */
  variant?: 'line' | 'pill'
  label: string
  className?: string
}

/**
 * Radix Tabs: arrow-key navigation and correct tab/tabpanel wiring come for
 * free, which matters for the study-detail 5-tab layout (DB-FR-002) and the
 * marketing feature showcase.
 */
export function Tabs({
  items,
  value,
  defaultValue,
  onValueChange,
  variant = 'line',
  label,
  className,
}: TabsProps) {
  return (
    <RTabs.Root
      value={value}
      defaultValue={defaultValue ?? items[0]?.value}
      onValueChange={onValueChange}
      className={cn('flex flex-col gap-4', className)}
    >
      <RTabs.List
        aria-label={label}
        className={cn(
          'flex gap-1',
          variant === 'line' && 'border-b border-border-subtle',
          variant === 'pill' && 'w-fit rounded-md bg-surface-inset p-1',
        )}
      >
        {items.map((item) => (
          <RTabs.Trigger
            key={item.value}
            value={item.value}
            disabled={item.disabled}
            className={cn(
              'inline-flex items-center gap-2 whitespace-nowrap px-3 py-2 text-body-sm font-medium',
              'text-text-secondary transition-colors duration-fast',
              'hover:text-text-primary disabled:pointer-events-none disabled:opacity-50',
              'data-[state=active]:text-text-primary',
              variant === 'line' &&
                '-mb-px border-b-2 border-transparent data-[state=active]:border-brand-primary',
              variant === 'pill' &&
                'rounded-sm data-[state=active]:bg-surface-raised data-[state=active]:shadow-sm',
              focusRing,
            )}
          >
            {item.label}
            {item.adornment}
          </RTabs.Trigger>
        ))}
      </RTabs.List>

      {items.map((item) => (
        <RTabs.Content
          key={item.value}
          value={item.value}
          className={cn('outline-none', focusRing)}
        >
          {item.content}
        </RTabs.Content>
      ))}
    </RTabs.Root>
  )
}
